import React, { type ReactNode } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { LoadingScreen } from "../components/LoadingScreen";
import {LoginPage} from '../components/LoginPage';
import { RegisterPage } from "../components/RegisterPage";

export interface ProtectedRouteProps {
  redirectTo?: string;
  fallback?: ReactNode;
}

/**
 * `react-router-dom` v6 layout route that redirects to the login page,
 * preserving the attempted location in `state.from` so the login page can
 * send the user back afterwards. Only import from `@ferrumec/auth/router`
 * if your app already depends on `react-router-dom` — the rest of the
 * package has no router dependency.
 *
 * ```tsx
 * <Route element={<ProtectedRoute />}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * ```
 */
export function ProtectedRoute({ redirectTo = "/auth/login", fallback }: ProtectedRouteProps) {
  const { authenticated, loading } = useAuth();

  if (loading) return <>{fallback ?? <LoadingScreen />}</>;
  if (!authenticated) return <Navigate to={redirectTo} />;
  return <Outlet />;
}

export interface PublicOnlyRouteProps {
  redirectTo?: string;
  fallback?: ReactNode;
}

/** Inverse of `ProtectedRoute` — for the login page itself, so an already-authenticated user skips it. */
export function PublicOnlyRoute({ redirectTo = "/", fallback }: PublicOnlyRouteProps) {
  const { authenticated, loading } = useAuth();

  if (loading) return <>{fallback ?? <LoadingScreen />}</>;
  if (authenticated) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
}

export function AuthRoutes(){
  return <Routes>
    <Route path="/auth/login" element={<LoginPage/>}/>
    <Route path="/auth/register" element={<RegisterPage/>}/>
  </Routes>
}
